// The toss, the venue, and whether either of them is worth a price.
//
// THE HYPOTHESIS. Cricket is the one sport where a coin is flipped before play
// and the winner of it chooses the conditions the first innings is played in.
// The published finding is small and real — a toss winner wins about 2.8% more
// often than a coin would say, and about 5.9% more in day-night ODIs — and
// nobody has published whether the AUSTRALIAN MARKET prices it. That is the
// question; this model is the instrument.
//
// WHAT IT READS, off the features bag a stored line carries
// (`features.cricsheet`, written by scripts/ingest-cricsheet.mjs):
//
//   tossHome     1 when the HOME side won the toss, 0 when the away side did.
//   bat          1 when the toss winner chose to bat, 0 when it chose to field.
//   tossHomeBat  the two multiplied, because "home won the toss and batted" is
//                a different thing from either half on its own: the choice is
//                made in the knowledge of the ground, and the ground is the
//                home side's.
//   homeKnown    1 where the source could say who was at home. Cricsheet does
//                not carry it, so it is derived from the city (see the ingest),
//                and a match where it could not be derived has an ARBITRARY
//                home side. Without this column the fit would read those
//                matches as evidence that home advantage is zero, which is a
//                claim the data never made.
//   dayNight     +1 night, −1 day, 0 unknown. From Cricsheet it is ALWAYS zero:
//                the source carries a date and no start time. The column stays
//                because the feature is the interesting one and a second source
//                may fill it; while it is constant the ridge shrinks it to
//                nothing, and `state.dayNightRows` reports that it was never
//                anything else.
//   format       a one-hot over the sports the fit saw (bbl, cricket_t20,
//                cricket_odi, cricket_test), because a toss in a Test is not a
//                toss in a Twenty20 and a fit pooled across them fits the
//                average of three different games.
//
// THE TARGET IS THE RESULT, which makes this the KIND of model the engine is
// most sceptical of. `closeLine.js` exists because a model fitted on outcomes
// loses to the market; this one is fitted on outcomes deliberately, because the
// claim is not "we forecast cricket" but "here is a fact about the fixture that
// the price may not contain". The encompassing gate (`encompassing.js`) is
// exactly the right test for that: given the market's own price, does knowing
// the toss add anything? Until that clears t = 1.96 on a walk-forward sample,
// this model prints nothing.
//
// AND IT CANNOT CLEAR IT YET. The gate needs a market price beside every
// result, and the cricket history has none: Cricsheet is scorecards, and the
// app's own boards have been keeping closing prices for cricket only since the
// drift history began. So the honest state of this model on the day it was
// written is FITTED AND UNGATED, and `docs/RESEARCH-2026-09-16-cricket-toss.md`
// says what the fit found and what still cannot be said.
//
// DRAWS ARE NOT IN THE FIT. A Test drawn over five days is not a home win and
// not an away win, and a model that called it half of each would be inventing a
// third outcome to fit. The fit uses decided matches only and `predict` answers
// the home and away sides alone: on a three-way Test market the draw price is
// not this model's to give, and a strategy that needs one will not get a leg.

import { assertBefore } from './base.js';
import { fitRidge, predictRidge, standardise } from './ridge.js';
import { CRICKET_SPORTS } from '../teams.js';

/** The feature names, in the order a row is built. The one-hot follows them. */
export const BASE_FEATURES = ['tossHome', 'bat', 'tossHomeBat', 'homeKnown', 'dayNight'];

/** Below this many decided matches the ridge is fitting noise. */
export const MIN_FIT_ROWS = 300;

/** The ridge penalty on the standardised features. Small: it breaks ties. */
const LAMBDA = 1e-3;

const DEFAULT_PARAMS = { lambda: LAMBDA, minRows: MIN_FIT_ROWS };

/** The features this model reads, whoever published them. */
export const tossFeatures = (match) => match?.features?.cricsheet ?? null;

/** +1 a night match, −1 a day match, 0 where nobody said. */
function dayNightOf(features) {
  const value = features?.dayNight;
  if (value === true || value === 'night') return 1;
  if (value === false || value === 'day') return -1;
  return 0;
}

/**
 * One feature row for one match, or null where the toss is not on it.
 * `sports` is the one-hot's alphabet, carried on the state so a fit and a
 * prediction build the same width of row.
 */
export function featureRow(match, sports) {
  const features = tossFeatures(match);
  const toss = features?.toss;
  if (!toss || (toss.winner !== 'home' && toss.winner !== 'away')) return null;

  const tossHome = toss.winner === 'home' ? 1 : 0;
  const bat = toss.decision === 'bat' ? 1 : 0;
  const x = [
    tossHome,
    bat,
    tossHome * bat,
    features.homeKnown ? 1 : 0,
    dayNightOf(features),
  ];
  for (const sport of sports) x.push(match?.sport === sport ? 1 : 0);
  return x;
}

/** `home` / `away` where the match was decided, null where it was not. */
export function decidedWinner(match) {
  const winner = match?.result?.winner;
  return winner === 'home' || winner === 'away' ? winner : null;
}

/** Every cricket sport in a history, in the registry's order. */
const sportsIn = (matches) => CRICKET_SPORTS
  .filter((sport) => matches.some((m) => m?.sport === sport));

export const cricketToss = {
  id: 'cricket-toss',
  version: 1,
  sports: CRICKET_SPORTS,
  needsFit: true,
  defaultParams: { ...DEFAULT_PARAMS },

  /**
   * Walk the history, build one row per DECIDED match that carries a toss, and
   * ridge-regress the home win on it.
   *
   * `asOf` is the moment the state claims to be true at, and `strict` refuses
   * outright rather than filtering — a future match in a walk-forward training
   * set is a bug in the harness, not a row to drop.
   */
  fit(history, asOf, params = {}, { strict = false } = {}) {
    const p = { ...DEFAULT_PARAMS, ...params };
    const matches = assertBefore(history, asOf, strict)
      .filter((m) => CRICKET_SPORTS.includes(m?.sport));
    const sports = sportsIn(matches);
    const width = BASE_FEATURES.length + sports.length;

    const rows = [];
    let withToss = 0;
    let drawn = 0;
    let dayNightRows = 0;
    let homeKnownRows = 0;
    let tossWon = 0;
    for (const match of matches) {
      const x = featureRow(match, sports);
      if (!x) continue;
      withToss += 1;
      if (x[4] !== 0) dayNightRows += 1;
      if (x[3] === 1) homeKnownRows += 1;
      const winner = decidedWinner(match);
      if (!winner) { drawn += 1; continue; }
      const y = winner === 'home' ? 1 : 0;
      // Counted, not fitted: the headline every research run wants is how often
      // the side that won the toss won the match, and it is a fact about the
      // sample rather than a weight.
      if ((x[0] === 1) === (y === 1)) tossWon += 1;
      rows.push({ x, y });
    }

    const { means, sds } = standardise(rows, width);
    const scaled = rows.map(({ x, y }) => ({
      x: x.map((v, i) => (v - means[i]) / sds[i]),
      y,
    }));
    const weights = scaled.length >= p.minRows ? fitRidge(scaled, width, p.lambda) : null;

    return {
      weights,
      means,
      sds,
      sports,
      n: rows.length,
      matches: withToss,
      drawn,
      dayNightRows,
      homeKnownRows,
      tossWon,
      tossWinRate: rows.length ? tossWon / rows.length : null,
      params: p,
      fittedTo: new Date(asOf).toISOString(),
    };
  },

  /**
   * The probability this side wins, GIVEN that somebody does. Null where the
   * match carries no toss, where the fit never had enough rows to be a fit, or
   * where the selection is the draw — which this model has nothing to say about.
   */
  predict(state, match, selection) {
    if (selection !== 'home' && selection !== 'away') return null;
    const weights = state?.weights;
    if (!Array.isArray(weights) || !weights.length) return null;
    const x = featureRow(match, state.sports ?? []);
    if (!x || x.length !== weights.length - 1) return null;
    const pHome = predictRidge(weights, x, state.means ?? [], state.sds ?? []);
    if (pHome === null) return null;
    return selection === 'home' ? pHome : 1 - pHome;
  },
};
